import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatINR, formatPct, formatDateTime } from "@/lib/format";

interface Backtest {
  _id: string;
  name: string;
  strategy: string;
  startDate: string;
  endDate: string;
  initialCapital: number;
  finalCapital: number;
  totalReturn: number;
  maxDrawdown: number;
  sharpeRatio: number;
  winRate: number;
  totalTrades: number;
  createdAt: string;
}

export default function Backtests() {
  const { data: backtestsResp, isLoading, error } = useQuery<{ data: Backtest[] }>({
    queryKey: ["backtests"],
    queryFn: () => api.get("/backtests"),
  });
  const backtests = backtestsResp?.data ?? [];

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>
        Backtests
      </h1>

      <div className="card">
        {isLoading && <div style={{ padding: 20, color: "var(--text-muted)" }}>Loading backtests…</div>}
        {error && <div style={{ padding: 20, color: "var(--red-light)" }}>Error: {error.message}</div>}
        {backtests && backtests.length === 0 && (
          <div style={{ padding: 20, color: "var(--text-muted)" }}>No backtest results</div>
        )}
        {backtests && backtests.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Strategy</th>
                <th>Period</th>
                <th style={{ textAlign: "right" }}>Initial</th>
                <th style={{ textAlign: "right" }}>Final</th>
                <th style={{ textAlign: "right" }}>Return</th>
                <th style={{ textAlign: "right" }}>Max DD</th>
                <th style={{ textAlign: "right" }}>Sharpe</th>
                <th style={{ textAlign: "right" }}>Win %</th>
                <th style={{ textAlign: "right" }}>Trades</th>
              </tr>
            </thead>
            <tbody>
              {backtests.map((b) => (
                <tr key={b._id}>
                  <td style={{ fontWeight: 600 }}>{b.name}</td>
                  <td style={{ color: "var(--text-muted)" }}>{b.strategy}</td>
                  <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {formatDateTime(b.startDate).split(",")[0]} → {formatDateTime(b.endDate).split(",")[0]}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{formatINR(b.initialCapital)}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{formatINR(b.finalCapital)}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: b.totalReturn >= 0 ? "var(--green-light)" : "var(--red-light)" }}>
                    {formatPct(b.totalReturn)}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: "var(--red-light)" }}>
                    {formatPct(b.maxDrawdown)}
                  </td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{b.sharpeRatio.toFixed(2)}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{formatPct(b.winRate)}</td>
                  <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>{b.totalTrades}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
